import React, { useState, useEffect } from "react";
import { compose } from 'redux';
import { withRouter, Link } from "react-router-dom";
import {connect} from "react-redux";
import { is, toLower, pathOr, path } from "ramda";
import { Card } from "@maxmilhas/hangar-components-atomic/public";
import OrderResume from "../OrderResume";
import { checkoutCredentials, redirectAfterLogout, redirectToLogin } from "../../../shared/utils";
import MyAccountMenu from "../MyAccountMenu";
import {
  logoutUser,
  pingUser,
  getCovidLimitDateByAirline,
  calcOrderCancelation,
  getCancellationRules
} from "../../../shared/requests-api";
import CancellationBudgetOptions from "./CancellationBudgetOptions";
import FlightResume from "../FlightResume";
import CancellationBillingResume from "./organisms/CancellationBillingResume";
import CancellationRulesResume from "./organisms/CancellationRulesResume";
import CancellationProtocol from "./CancellationProtocol";
import moment from 'moment';
import { css } from 'emotion';
import CancellationCategories from "./CancellationCategories";
import _ from 'lodash';

const h1TitlePadding = `
  padding-top: 16px;
`;

export const CancellationWrapper = ({ orderToCancel, history }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalLimitDate, setFinalLimitDate] = useState('');
  const [finalLimitDateInFull, setFinalLimitDateInFull] = useState('');
  const [initialDate, setInitialDate] = useState('');
  const [issuedUntilDate, setIssuedUntilDate] = useState('');
  const [initialDateInFull, setInitialDateInFull] = useState('');
  const [dateLimitToShowWarning, setDateLimitToShowWarning] = useState('');
  const [useDynamicCancellation, setUseDynamicCancellation] = useState(true);
  const [useDynamicDate, setUseDynamicDate] = useState('');
  const [addDaysToDynamicDate, setAddDaysToDynamicDate] = useState('');
  const [isRebookingTransaction, setIsRebookingTransaction] = useState(null);
  const [secondLimitDate, setSecondLimitDate] = useState('');
  const [secondLimitDateInFull, setSecondLimitDateInFull] = useState('');
  const [rateLimitForDisplayingRefund, setRateLimitForDisplayingRefund] = useState('');
  let limitDateInFull = finalLimitDateInFull;

  useEffect(() => {
    window.scrollTo(0, 0);
    const token = checkoutCredentials().au;

    const handleCalculateCancellation = async (orderCode) => {
      try {
        const orderResponse = await calcOrderCancelation(token, orderCode);
        const cancellationProtocolRules = await getCancellationRules(token);
        setOrder({
          ...orderResponse.data,
          cancellationProtocolRules: cancellationProtocolRules.data
        });
      } catch (err) {
        history.push('/cliente/minhas-viagens-light');
      }
    }

    if (token) {
      pingUser(token)
        .then(response => {
          if (!response.data.success) {
            logoutUser()
              .then(() => {
                redirectAfterLogout(window);
              })
              .catch(error => {
                redirectToLogin(window);
                reject(error);
              });
          } else {
            if (orderToCancel) {
              setOrder(orderToCancel);
            } else {
              const queryString = window.location.search;
              const urlParams = new URLSearchParams(queryString);
              const orderCode = urlParams.get('ido');
              if (orderCode) {
                handleCalculateCancellation(orderCode);
              } else {
                history.push('/cliente/minhas-viagens-light');
              }
            }
          }
        })
        .catch(error => {
          redirectToLogin(window);
          reject(error);
        });
    } else {
      redirectToLogin(window);
    }
  }, []);

  useEffect(() => {
    if (order) {
      setIsRebookingTransaction(path(['stretches', 'rebooking', 'isRebookingTransaction'], order));

      const getAirlineCovidDates = airline => {
        if (!(/^(Gol|Latam|Azul)$/.test(airline))) {
          airline = 'default';
        }

        getCovidLimitDateByAirline()
          .then(response => {
            setUseDynamicCancellation(pathOr(true, ['data', 'configuration', 'useDynamicCancellation'], response));
            setFinalLimitDate(moment(response.data[toLower(airline)].date, "DD-MM-YYYY").format('YYYY-MM-DD'));
            setFinalLimitDateInFull(response.data[toLower(airline)].dateInFull);
            setInitialDate(moment(response.data[toLower(airline)].initialDate, "DD-MM-YYYY").format('YYYY-MM-DD'));
            setIssuedUntilDate(response.data[toLower(airline)].issuedUntilDate ? moment(response.data[toLower(airline)].issuedUntilDate, "DD-MM-YYYY").format('YYYY-MM-DD') : null);
            setInitialDateInFull(response.data[toLower(airline)].initialDateInFull);
            setDateLimitToShowWarning(moment(response.data[toLower(airline)].dateLimitToShowWarning, "DD-MM-YYYY").format('YYYY-MM-DD'));
            setUseDynamicDate(response.data[toLower(airline)].useDynamicDate);
            setAddDaysToDynamicDate(response.data[toLower(airline)].addDaysToDynamicDate);
            setLoading(false);
            setSecondLimitDate(response.data[toLower(airline)].secondLimitDate ? moment(response.data[toLower(airline)].secondLimitDate, "DD-MM-YYYY").format('YYYY-MM-DD') : null);
            setSecondLimitDateInFull(response.data[toLower(airline)].secondLimitDateInFull);
            setRateLimitForDisplayingRefund(response.data.rateLimitForDisplayingRefund);
          })
          .catch(err => {
            history.push('/cliente/minhas-viagens-light');
          });
      };
  
      getAirlineCovidDates(order.flights.outbound.airline);
    }
  }, [order]);

  const valueIsGreaterThanLimitForDisplayingRefund = () => {
    const total = parseFloat(order.order.billing.value) + parseFloat(order.order.billing.total_credit);

    return total > rateLimitForDisplayingRefund;
  };

  const getFinalLimitDate = () => {
    if (useDynamicDate) {
      return moment().add(addDaysToDynamicDate || 0, 'days').format('YYYY-MM-DD');
    }

    if (order.order && order.order.eticketCreated) {
      let eticketCreated = order.order.eticketCreated.split(" ")[0];
      if (eticketCreated > issuedUntilDate) {
        limitDateInFull = secondLimitDateInFull;
        return secondLimitDate;
      }
    }

    return finalLimitDate;
  };

  const isAfterCiaLimitDate = () => {
    const { flights } = order;

    let outboundDepartureDate = flights.outbound.departureDate.split(" ")[0];
    let inboundDepartureDate = flights.inbound && flights.inbound.departureDate.split(" ")[0];

    outboundDepartureDate = moment(outboundDepartureDate, "DD-MM-YYYY").format('YYYY-MM-DD');
    inboundDepartureDate = inboundDepartureDate && moment(inboundDepartureDate, "DD-MM-YYYY").format('YYYY-MM-DD');
    
    if (outboundDepartureDate > getFinalLimitDate()) {
      return true
    }

    return false;
  };

  const isBeforeCiaInitialDate = () => {
    const { flights } = order;

    let outboundDepartureDate = flights.outbound.departureDate.split(" ")[0];
    let inboundDepartureDate = flights.inbound && flights.inbound.departureDate.split(" ")[0];
    
    outboundDepartureDate = moment(outboundDepartureDate, "DD-MM-YYYY").format('YYYY-MM-DD');
    inboundDepartureDate = inboundDepartureDate && moment(inboundDepartureDate, "DD-MM-YYYY").format('YYYY-MM-DD');

    if (outboundDepartureDate < initialDate) {
      if (inboundDepartureDate && inboundDepartureDate > initialDate) {
        return false;
      }

      return true;
    }

    return false;
  };

  const isInCovidPeriod = () => {
    const isBefore = isBeforeCiaInitialDate();

    const isAfter = isAfterCiaLimitDate();

    const isNotCovidPeriod = isBefore || isAfter;

    return !isNotCovidPeriod;
  }

  /**
   * Considerando no show os voo que já passaram ou que faltam menos de 4 horas para ocorrer
   */
  const isNoShow = () => {
    const { stretches } = order;

    // azul aceita solicitações quando o voo ja passou, então não é considerado no-show
    if (_.get(stretches, 'outbound.airline', '').toLowerCase() === 'azul') {
      return false;
    }

    const now = moment().utc();

    const outboundDepartureDate = moment(stretches.outbound.departure).utc();

    if (!stretches.inbound) {
      return outboundDepartureDate.diff(now, 'hours') < 4;
    }

    const inboundDepartureDate = moment(stretches.inbound.departure).utc();

    return outboundDepartureDate.diff(now, 'hours') < 4 && inboundDepartureDate.diff(now, 'hours') < 4;
  };

  const checkForAlertModal = () => {
    const { flights } = order;
    const limitDate = getFinalLimitDate();

    if (limitDate === '' || dateLimitToShowWarning === '') return false;
    const departureDate = moment(flights.outbound.departureDate.split(" ")[0], "DD-MM-YYYY");
    return departureDate.isSameOrAfter(moment(dateLimitToShowWarning)) && departureDate.isSameOrBefore(moment(limitDate));
  };

  const check24HoursRule = () => order.cancellationData && order.cancellationData.labels.name && order.cancellationData.labels.name.includes("24h");

  return (
    <div id="cancellationPage" className="tripspage container">
      <div className="row">
        <aside className="col-xs-16 col-md-4">
          <MyAccountMenu/>
        </aside>

        <section className="col-xs-16 col-md-12">
          {loading && (
            <article className="new-card">
              <div className="new-card__content">
                <div className="loader-container">
                  <i className="loader loader-fetching center-block" />
                </div>
              </div>
            </article>
          )}

          {(!loading && order) && (
            <>
              <div className="return-row">
                <i className="icon icon-arrow-left return-icon"/>
                <Link
                  to="/cliente/minhas-viagens-light"
                  title="Voltar"
                  className="return-text"
                  data-action="my-trips-cancellation-page-return"
                >
                  Voltar
                </Link>
              </div>

              <div className="row">
                <div className="">
                  <div className="col-xs-16">
                    <Card className="details-wrapper">
                      <div>
                        <OrderResume order={is(Object, order) ? order.order : undefined}/>
                        <FlightResume
                          flights={order.flights}
                          passengers={order.passengers}
                        />
                      </div>
                    </Card>

                    {!isRebookingTransaction && <h1 className={`subtitle ${css`${h1TitlePadding}`}`}>Pedido de alteração</h1>}

                    {/*<CancellationProtocol order={order} title='Alteração por motivo excepcional (Ex: Concurso público, grandes eventos, etc)'/>*/}

                    {isRebookingTransaction && !isNoShow() && (
                      <CancellationBudgetOptions order={order} hideTitle />
                    )}

                    {!isRebookingTransaction && !isNoShow() && check24HoursRule() && (
                      <CancellationBillingResume order={order} />
                    )}

                    {!isRebookingTransaction && !isNoShow() && !check24HoursRule() && (
                      <>
                        {(!useDynamicCancellation && !isBeforeCiaInitialDate()) && <CancellationProtocol order={order} isAfterCiaDate={isAfterCiaLimitDate()} isCorona={true} noShow={isNoShow()} title={limitDateInFull} showAlertModal={checkForAlertModal()} />}

                        {(!isInCovidPeriod() || valueIsGreaterThanLimitForDisplayingRefund()) && <CancellationBillingResume order={order} /> }

                        {(!isInCovidPeriod() || valueIsGreaterThanLimitForDisplayingRefund()) && (order.ruleCancellationData && order.ruleCancellationData.outbound) && 
                          <CancellationRulesResume rules={order.ruleCancellationData.outbound} direction="outbound" />
                        }
                        {(!isInCovidPeriod() || valueIsGreaterThanLimitForDisplayingRefund()) && (order.ruleCancellationData && order.ruleCancellationData.inbound) && 
                          <CancellationRulesResume rules={order.ruleCancellationData.inbound} direction="inbound" /> 
                        }
                        {(!isInCovidPeriod() || valueIsGreaterThanLimitForDisplayingRefund()) && <CancellationBudgetOptions order={order} />}
                      </>
                    )}

                    {useDynamicCancellation && !isRebookingTransaction && <CancellationCategories order={order} />}
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  orderToCancel: state.cancellation.orderToCancel
});

export default compose(
  withRouter,
  connect(mapStateToProps, {})
)(CancellationWrapper);
